/* Карточка кампании: цена, продажи, GMV, срок, воронка, контрольная группа, слоты, остановка и страница клиента. */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { DAY, OFFER_STATUS, dateTime, isActive, money, offerStatus, pct, startOfDay, sum, time, totals, type Offer } from "@okno/shared";
import { api } from "../../lib/api";
import { locKey, useBoard, useNow, useSession } from "../../lib/session";
import { Funnel } from "../../ui/charts";
import { Icon } from "../../ui/Icon";
import { Empty, Spinner } from "../../ui/kit";
import { Drawer, useCloseLayer } from "../../ui/layers";
import { useToast } from "../../ui/toast";
import { minutesLeft } from "./CampaignsTable";
import { LIST_DAYS, useOffersList } from "./queries";

/** Если кампании нет в списке экрана (старая — открыта из заказа), ищем глубже. */
const DEEP_DAYS = 365;
const CHANNEL_LABEL: Record<string, string> = { sms: "SMS", wa: "WhatsApp", push: "push" };

/** Кампания по id: сначала из списка экрана, при промахе — из списка за год. */
function useOffer(offerId: string): { offer: Offer | null; loading: boolean } {
  const recent = useOffersList(LIST_DAYS);
  const missing = !!recent.data && !recent.data.some((o) => o.id === offerId);
  const deep = useOffersList(DEEP_DAYS, missing);
  const offer = recent.data?.find((o) => o.id === offerId) || deep.data?.find((o) => o.id === offerId) || null;
  return { offer, loading: !offer && (recent.isLoading || (missing && deep.isLoading)) };
}

export function OfferDrawer({ offerId }: { offerId: string }) {
  const { offer, loading } = useOffer(offerId);
  if (!offer)
    return (
      <Drawer title="Кампания">
        {loading ? <Spinner /> : <Empty icon="alert" title="Кампания не найдена" text="Возможно, она удалена или относится к другой точке." />}
      </Drawer>
    );
  return <OfferCard offer={offer} />;
}

function OfferCard({ offer: o }: { offer: Offer }) {
  const { boot, locationId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const close = useCloseLayer();
  const nowTs = useNow();
  const [stopping, setStopping] = useState(false);
  // заказы кампании лежат в её слотах — берём доску дней этих слотов
  const starts = o.slots.length ? o.slots.map((s) => s.start) : [o.createdAt];
  const from = startOfDay(Math.min(...starts));
  const board = useBoard(from, startOfDay(Math.max(...starts)) + DAY);
  const orders = (board.data?.orders || []).filter((x) => x.offerId === o.id);

  const svc = boot.services.find((x) => x.id === o.serviceId);
  const st = offerStatus(o, nowTs);
  const f = o.funnel;
  const gmv = sum(
    orders.filter((x) => x.status === "done"),
    (x) => totals(x, boot.settings).total,
  );
  const testConv = f.delivered ? f.paid / f.delivered : 0;
  const link = o.previewToken ? "/o/" + encodeURIComponent(o.previewToken) : null;

  const stop = async () => {
    setStopping(true);
    try {
      await api.post("/locations/" + locationId + "/offers/" + o.id + "/stop");
      void qc.invalidateQueries({ queryKey: locKey(locationId) });
      close();
      toast("Предложение остановлено, ссылки больше не работают");
    } catch (e) {
      toast.error(e);
      setStopping(false);
    }
  };

  const foot = (
    <>
      {st === "active" ? (
        <button className="btn btn-danger" disabled={stopping} onClick={() => void stop()}>
          Остановить
        </button>
      ) : null}
      <span style={{ flex: 1 }} />
      {link ? (
        <a className="btn btn-primary" href={link} target="_blank" rel="noopener noreferrer">
          <Icon name="external" size={16} />
          Открыть как клиент
        </a>
      ) : null}
    </>
  );

  return (
    <Drawer title={svc?.name || "Кампания"} sub={OFFER_STATUS[st].label + " · " + dateTime(o.createdAt) + " · " + (CHANNEL_LABEL[o.channel] || o.channel)} foot={foot}>
      <div className="grid grid-2" style={{ gap: 8 }}>
        <div className="mini">
          <span className="kpi-label">Цена</span>
          <strong className="num">{money(o.price)}</strong>
          {o.price < o.basePrice ? <span className="cell-sub">вместо {money(o.basePrice)}</span> : null}
        </div>
        <div className="mini">
          <span className="kpi-label">Продано</span>
          <strong className="num">
            {f.paid} из {o.limit}
          </strong>
        </div>
        <div className="mini">
          <span className="kpi-label">Выручка (GMV)</span>
          <strong className="num">{money(gmv || f.completed * o.price)}</strong>
        </div>
        <div className="mini">
          <span className="kpi-label">Срок</span>
          <strong>{st === "active" ? "ещё " + minutesLeft(o, nowTs) + " мин" : OFFER_STATUS[st].label}</strong>
        </div>
      </div>
      <section>
        <div className="section-title">Воронка</div>
        <Funnel
          steps={[
            { label: "Отправлено", value: f.sent },
            { label: "Доставлено", value: f.delivered },
            { label: "Открыли", value: f.opened },
            { label: "Забронировали", value: f.booked },
            { label: "Оплатили", value: f.paid },
            { label: "Приехали", value: f.completed },
          ]}
        />
      </section>
      <section>
        <div className="section-title">Контрольная группа</div>
        <div className="panel panel-body">
          <dl className="kv">
            <dt>Конверсия получателей</dt>
            <dd>{pct(testConv, 1)}</dd>
            <dt>Контроль ({o.control} чел., без сообщения)</dt>
            <dd>{pct(o.controlConv || 0, 1)}</dd>
            <dt>Инкрементальный эффект</dt>
            <dd style={{ color: "var(--st-ready)" }}>+{pct(Math.max(0, testConv - (o.controlConv || 0)), 1)}</dd>
          </dl>
          <p className="cell-sub" style={{ marginTop: 8 }}>
            Контрольная группа не получает сообщение — так видно, сколько заказов появилось именно благодаря предложению.
          </p>
        </div>
      </section>
      {o.slots.length ? (
        <section>
          <div className="section-title">Слоты</div>
          <div className="row">
            {o.slots.map((sl) => {
              const taken = orders.some((x) => x.start === sl.start && x.bayId === sl.bayId && isActive(x, nowTs));
              const bay = boot.bays.find((b) => b.id === sl.bayId);
              return (
                <span key={sl.bayId + "|" + sl.start} className={"chip " + (taken ? "chip-plus" : "chip-signal")}>
                  {time(sl.start)} · {bay?.name || "—"}
                  {taken ? " · продан" : ""}
                </span>
              );
            })}
          </div>
        </section>
      ) : null}
      {st === "active" && link ? (
        <div className="callout callout-signal">
          <Icon name="phone" size={16} />
          <span>Откройте страницу клиента в новой вкладке и забронируйте слот — заказ появится в календаре и в этой воронке.</span>
        </div>
      ) : null}
    </Drawer>
  );
}
